import { ReactNode } from 'react';
import { css as emotionCSS, Theme } from '@emotion/react';
import { useMediaQuery } from '@mildang/mui';
import { Typography as _Typography } from '@mildang/mui';
import Image from 'components/molecules/Image';
import { interopRequireDefault } from 'utils/interopRequireDefault';

const Typography = interopRequireDefault(_Typography);

export interface PosterTemplateProps {
  imageSrc: string;
  alt?: string;
  title?: ReactNode;
  description?: ReactNode;
  action?: ReactNode;
  className?: string;
  imageWidth?: number;
  imageHeight?: number;
}

const rootStyle = (theme: Theme) => emotionCSS`
  display: grid;
  place-items: center;
  width: 100%;
  height: 100%;
  text-align: center;
  background-color: ${theme.palette.background.secondary};
`;

const titleTextStyle = (theme: Theme) => emotionCSS`
  margin-top: ${theme.spacing(3)};

  ${theme.breakpoints.down('sm')} {
    margin-top: ${theme.spacing(2)};
  }
`;

const descriptionTextStyle = (theme: Theme) => emotionCSS`
  display: inline-block;
  margin-top: ${theme.spacing(2)};
  white-space: pre-wrap;

  ${theme.breakpoints.down('sm')} {
    margin-top: ${theme.spacing(1)};
  }
`;

const actionBoxStyle = (theme: Theme) => emotionCSS`
  margin-top: ${theme.spacing(4)};

  ${theme.breakpoints.down('sm')} {
    margin-top: ${theme.spacing(3)};
  }
`;

function PosterTemplate(props: PosterTemplateProps) {
  const { imageWidth, imageHeight, imageSrc, alt, title, description, action, className } = props;

  const isMobile = useMediaQuery<Theme>((theme) => theme.breakpoints.down('sm'));

  return (
    <div css={rootStyle} className={className}>
      <div>
        <Image
          src={imageSrc}
          alt={alt ?? 'poster image'}
          layout="fixed"
          width={imageWidth ?? (isMobile ? 318 : 424)}
          height={imageHeight ?? (isMobile ? 210 : 280)}
        />
        <span>
          {title && (
            <Typography
              color="text.primary"
              variant={isMobile ? 'h5' : 'h2'}
              align="center"
              css={titleTextStyle}
            >
              {title}
            </Typography>
          )}
        </span>
        <span>
          {description && (
            <Typography
              color="text.secondary"
              variant={isMobile ? 'body4' : 'body1'}
              align="center"
              css={descriptionTextStyle}
            >
              {description}
            </Typography>
          )}
        </span>
        {action && <div css={actionBoxStyle}>{action}</div>}
      </div>
    </div>
  );
}

export default PosterTemplate;
